import { Calendar, BookOpen, BarChart3, Award, Edit, Trash2, Download, CheckCircle, MessageSquare, Video, ClipboardCheck, FileText, MoreHorizontal, Target, ChevronRight, Filter, Plus } from 'lucide-react';
import HomeworkStatusFilter, { HomeworkStatusFilter as StatusFilterType } from './components/homework/HomeworkStatusFilter';
import HomeworkDayGroups from './components/homework/HomeworkDayGroups';
import DeficienciesView from './components/deficiencies/DeficienciesView';
import TabPage, { TabItemProps } from '../../components/dynamic/Tab';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { ErrorBookletView } from './components/error-booklet';
import ReportsView from './components/reports/ReportsView';
import MyReportsView from './components/reports/MyReportsView';
import checkPermission from '../../utils/permission';
import { ApiService } from '../../services/api';
import { useModal } from '../../hooks/useModal';
import { useNavigate } from 'react-router-dom';

interface Course {
  id: string;
  title: string;
  description?: string;
  coverMedia?: {
    url: string;
  };
  isActive: boolean;
  isCertificate: boolean;
  isDownloadable: boolean;
  isCommentable: boolean;
  updatedAt: string;
  createdAt: string;
}

export default function LessonsPage() {
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useModal();

  const [selectedHomeworkStatus, setSelectedHomeworkStatus] = useState<StatusFilterType>("all");
  const [homeworkFilterOpen, setHomeworkFilterOpen] = useState(true);
  const [homeworkStartDate, setHomeworkStartDate] = useState<string | null>(null);
  const [homeworkEndDate, setHomeworkEndDate] = useState<string | null>(null);
  const [homeworkRefreshTrigger, setHomeworkRefreshTrigger] = useState(0);
  const [expandedLessonGroups, setExpandedLessonGroups] = useState<Record<string, boolean>>({});

  const toggleLessonGroup = useCallback((weekKey: string, lessonId: string) => {
    const key = `${weekKey}:${lessonId}`;
    setExpandedLessonGroups((prev) => ({
      ...prev,
      [key]: !(prev[key] ?? true),
    }));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hasFocus()) {
        setHomeworkRefreshTrigger((prev) => prev + 1);
      }
    }, 300000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const isMobile = window.matchMedia && window.matchMedia('(max-width: 640px)').matches;
        if (isMobile) setHomeworkFilterOpen(false);
      }
    } catch (_) { }
  }, []);

  const [hasEditPermission, setHasEditPermission] = useState(false);
  const [hasDeletePermission, setHasDeletePermission] = useState(false);
  const [hasAddPermission, setHasAddPermission] = useState(false);
  const [canLessonEdit, setCanLessonEdit] = useState(false);
  const [canLessonDelete, setCanLessonDelete] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);

  const handleDropdownToggle = (courseId: string) => {
    setDropdownOpen(dropdownOpen === courseId ? null : courseId);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".dropdown-container")) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setHasEditPermission(checkPermission("lesson_update"));
    setHasDeletePermission(checkPermission("lesson_delete"));
    setHasAddPermission(checkPermission("lesson_add"));
    setCanLessonEdit(checkPermission("lesson_update"));
    setCanLessonDelete(checkPermission("lesson_delete"));
  }, []);

  const isParentOnly =
    checkPermission("parent_report_list") &&
    !checkPermission("homework_report_list") &&
    !checkPermission("test_report_list") &&
    !checkPermission("homework_list");

  const handleCourseClick = (course: Course) => {
    // Öğrenci ders kartından doğrudan oynatıcıya gider ve kaldığı yerden devam eder;
    // içerik yöneten roller (öğretmen/admin) ve veli mevcut detay sayfasında kalır.
    const managesContent = checkPermission("lesson_update") || checkPermission("lesson_add");
    if (managesContent || isParentOnly) {
      navigate(`/lessons/detail/${course.id}`);
    } else {
      navigate(`/lessons/play/${course.id}`);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getRemainingTime = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - now.getTime();

    if (diffTime <= 0) {
      return "Süresi Geçti";
    }

    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return "Bugün Bitiyor";
    } else if (diffDays === 1) {
      return "1 Gün Kaldı";
    } else if (diffDays <= 7) {
      return `${diffDays} Gün Kaldı`;
    } else if (diffDays <= 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? "1 Hafta Kaldı" : `${weeks} Hafta Kaldı`;
    } else {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? "1 Ay Kaldı" : `${months} Ay Kaldı`;
    }
  };

  const formatDeadlineDate = (deadline: string) => {
    return new Date(deadline).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getGradientByLessonId = (lessonId: string) => {
    const numericId = lessonId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const gradientIndex = numericId % 20;

    const gradients = [
      "from-blue-600 via-blue-500 to-indigo-500",
      "from-emerald-600 via-teal-500 to-cyan-500",
      "from-purple-600 via-violet-500 to-indigo-500",
      "from-teal-600 via-emerald-500 to-green-500",
      "from-indigo-600 via-blue-500 to-sky-500",
      "from-cyan-600 via-teal-500 to-emerald-500",
      "from-violet-600 via-purple-500 to-pink-500",
      "from-sky-600 via-blue-500 to-indigo-500",
      "from-green-600 via-emerald-500 to-teal-500",
      "from-slate-600 via-blue-500 to-indigo-500",
      "from-rose-500 via-pink-500 to-purple-500",
      "from-orange-500 via-amber-500 to-yellow-500",
      "from-emerald-500 via-green-500 to-teal-500",
      "from-blue-500 via-indigo-500 to-purple-500",
      "from-teal-500 via-cyan-500 to-sky-500",
      "from-purple-500 via-indigo-500 to-blue-500",
      "from-amber-500 via-orange-500 to-red-500",
      "from-green-500 via-teal-500 to-cyan-500",
      "from-indigo-500 via-purple-500 to-violet-500",
      "from-slate-500 via-gray-500 to-zinc-500",
    ];

    return gradients[gradientIndex];
  };

  const handleDeleteLesson = async (course: Course) => {
    const confirmed = await showConfirm({
      title: "Dersi Sil",
      message: `"${course.title}" dersini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`,
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (!confirmed) return;

    try {
      await ApiService.deleteLesson(course.id);
      window.location.reload();
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error.message || "Ders silinirken bir hata oluştu. Lütfen tekrar deneyin.",
        type: "error",
      });
    }
  };

  const coursesActions = useMemo(
    () => (
      <div className="flex flex-row flex-wrap gap-2">
        <a
          href="/lessons/new"
          className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium bg-green-600 text-white hover:bg-green-700 border border-green-600 flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2">
          <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <span>Yeni Ders</span>
        </a>
      </div>
    ),
    []
  );
  const homeworksActions = useMemo(
    () => (
      <div className="flex flex-row flex-wrap gap-2">
        <button
          onClick={() => setHomeworkFilterOpen((prev) => !prev)}
          className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 ${homeworkFilterOpen
            ? "bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
            : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
            }`}
          title="Filtrele"
          aria-pressed={homeworkFilterOpen}>
          <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <span>Filtrele</span>
        </button>
      </div>
    ),
    [homeworkFilterOpen]
  );
  const coursesTab: TabItemProps = useMemo(
    () => ({
      type: "grid",
      title: "Dersler",
      path: "lessons",
      actions: hasAddPermission ? coursesActions : null,
      isSearch: true,
      showToggle: true,
      item: (course: Course, viewMode = "grid") => {
        if (viewMode === "grid") {
          return (
            <div
              key={course.id}
              className="bg-white rounded-xl shadow-md border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden group mb-6 flex flex-col h-full">
              <div
                className={`relative h-48 bg-gradient-to-br ${getGradientByLessonId(course.id)} overflow-hidden cursor-pointer`}
                onClick={() => handleCourseClick(course)}>
                {course.coverMedia?.url ? (
                  <img
                    src={course.coverMedia?.url}
                    alt={course.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="w-20 h-20 text-white opacity-90" />
                  </div>
                )}
              </div>

              <div className="p-6 flex flex-col flex-1">
                <div className="mb-3">
                  <h3
                    className="font-bold text-xl text-gray-900 mb-2 group-hover:text-purple-600 transition-colors cursor-pointer"
                    onClick={() => handleCourseClick(course)}
                    style={{
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                      overflow: "hidden",
                    }}>
                    {course.title}
                  </h3>

                  {course.description && (
                    <p
                      className="text-gray-600 text-sm cursor-pointer"
                      onClick={() => handleCourseClick(course)}
                      style={{
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 2,
                        overflow: "hidden",
                      }}>
                      {course.description}
                    </p>
                  )}
                </div>

                <div className="flex gap-3 items-center mt-auto">
                  <button
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-purple-600 text-white px-4 py-3 rounded-xl text-sm font-semibold hover:bg-purple-700 shadow-lg transition-colors"
                    onClick={() => handleCourseClick(course)}>
                    <BookOpen className="w-4 h-4" />
                    Derse Git
                  </button>
                  {(canLessonEdit || canLessonDelete) && (
                    <div className="relative dropdown-container">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDropdownToggle(course.id);
                        }}
                        className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                        <MoreHorizontal className="h-5 w-5" />
                      </button>
                      {dropdownOpen === course.id && (
                        <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                          <div className="py-1">
                            {canLessonEdit && (
                              <a
                                href={`/lessons/edit/${course.id}`}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50">
                                <Edit className="w-4 h-4" />
                                Düzenle
                              </a>
                            )}
                            {canLessonDelete && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/lessons/${course.id}/delete`);
                                }}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                                Sil
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div key={course.id} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow mb-2">
              <div className="flex flex-col lg:grid lg:grid-cols-12 gap-3 lg:gap-3 lg:items-center">
                <div className="lg:col-span-4 flex items-start gap-3 min-w-0">
                  <div
                    className={`relative w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br ${getGradientByLessonId(
                      course.id
                    )} rounded-lg overflow-hidden flex-shrink-0 cursor-pointer`}
                    onClick={() => handleCourseClick(course)}>
                    {course.coverMedia?.url ? (
                      <img src={course.coverMedia?.url} alt={course.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-white opacity-80" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3
                      className="text-base sm:text-lg font-semibold text-gray-900 hover:text-purple-600 transition-colors cursor-pointer break-words mb-1"
                      onClick={() => handleCourseClick(course)}>
                      {course.title}
                    </h3>
                    {course.description && (
                      <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 lg:line-clamp-1">{course.description}</p>
                    )}
                  </div>
                </div>

                <div className="lg:col-span-5 flex flex-wrap items-center gap-2 min-w-0">
                  {course.isActive && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm max-w-full truncate">
                      <CheckCircle className="w-3 h-3 flex-shrink-0" />
                      <span className="inline md:hidden lg:inline">Aktif</span>
                    </span>
                  )}
                  {course.isCertificate && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-sm max-w-full truncate">
                      <Award className="w-3 h-3 flex-shrink-0" />
                      <span className="inline md:hidden lg:inline">Sertifikalı</span>
                    </span>
                  )}
                  {course.isDownloadable && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm max-w-full truncate">
                      <Download className="w-3 h-3 flex-shrink-0" />
                      <span className="inline md:hidden lg:inline">İndirilebilir</span>
                    </span>
                  )}
                  {course.isCommentable && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-sm max-w-full truncate">
                      <MessageSquare className="w-3 h-3 flex-shrink-0" />
                      <span className="inline md:hidden lg:inline">Yorumlanabilir</span>
                    </span>
                  )}
                </div>

                <div className="lg:col-span-3 flex items-center justify-start lg:justify-end gap-2 flex-wrap flex-shrink-0">
                  <button
                    className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors whitespace-nowrap"
                    onClick={() => handleCourseClick(course)}>
                    <BookOpen className="w-4 h-4" />
                    Derse Git
                  </button>
                  {(canLessonEdit || canLessonDelete) && (
                    <div className="relative dropdown-container flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDropdownToggle(course.id);
                        }}
                        className="p-1.5 rounded-full hover:bg-gray-100">
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                      {dropdownOpen === course.id && (
                        <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg z-[9999] border border-gray-200">
                          <div className="py-1">
                            {canLessonEdit && (
                              <a
                                href={`/lessons/edit/${course.id}`}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50">
                                <Edit className="w-4 h-4" />
                                Düzenle
                              </a>
                            )}
                            {canLessonDelete && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/lessons/${course.id}/delete`);
                                }}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                                Sil
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }
      },
      __visible: !isParentOnly,
    }),
    [coursesActions, handleCourseClick, hasAddPermission, isParentOnly]
  );
  const deficienciesTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Eksikler",
      path: "deficiencies",
      isSearch: false,
      showToggle: false,
      customComponent: <DeficienciesView />,
      __visible: !isParentOnly,
    }),
    [isParentOnly]
  );

  const errorBookletTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Hata Kitapçığı",
      path: "error-booklet",
      isSearch: false,
      showToggle: false,
      customComponent: <ErrorBookletView />,
      __visible: !isParentOnly,
    }),
    [isParentOnly]
  );
  const reportsTab: TabItemProps = useMemo(() => {
    const hasHomeworkReportList = checkPermission("homework_report_list");
    const hasTestReportList = checkPermission("test_report_list");
    const hasStudentReportList = checkPermission("student_report_list");
    const hasParentReportList = checkPermission("parent_report_list");
    const canShowReports = hasHomeworkReportList || hasTestReportList || hasStudentReportList || hasParentReportList;

    return {
      type: "custom",
      title: "Raporlar",
      path: "reports",
      actions: (
        <button className="bg-white px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          Rapor Oluştur
        </button>
      ),
      isSearch: false,
      showToggle: false,
      customComponent: <ReportsView />,
      __visible: canShowReports as any,
    };
  }, []);

  // Raporlarım: rapor yetkisi olmayan (öğrenci) kendi raporunu görür; rapor yetkilisi "Raporlar"ı görür, veli kendi sekmesini.
  const myReportsTab: TabItemProps = useMemo(() => {
    const hasReportPermission =
      checkPermission("homework_report_list") ||
      checkPermission("test_report_list") ||
      checkPermission("student_report_list") ||
      checkPermission("parent_report_list");
    return {
      type: "custom",
      title: "Raporlarım",
      path: "my-reports",
      isSearch: false,
      showToggle: false,
      customComponent: <MyReportsView />,
      __visible: (!hasReportPermission && !isParentOnly) as any,
    };
  }, [isParentOnly]);

  const handleHomeworkStatusChange = useCallback((status: StatusFilterType) => {
    setSelectedHomeworkStatus(status);
    setHomeworkRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleHomeworkDateChange = useCallback((startDate: string | null, endDate: string | null) => {
    setHomeworkStartDate(startDate);
    setHomeworkEndDate(endDate);
    setHomeworkRefreshTrigger((prev) => prev + 1);
  }, []);

  const renderHomeworkStatusFilter = useCallback(() => {
    return (
      <HomeworkStatusFilter
        selectedStatus={selectedHomeworkStatus}
        onStatusChange={handleHomeworkStatusChange}
        startDate={homeworkStartDate}
        endDate={homeworkEndDate}
        onDateChange={handleHomeworkDateChange}
      />
    );
  }, [selectedHomeworkStatus, handleHomeworkStatusChange, homeworkStartDate, homeworkEndDate, handleHomeworkDateChange]);

  const getISOWeekStart = (year: number) => {
    const simple = new Date(year, 0, 4);

    const dayOfWeek = simple.getDay();
    const ISOWeekStart = new Date(simple);
    ISOWeekStart.setDate(simple.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    return ISOWeekStart;
  };

  const getISOWeekAndYear = (dateString: string): { week: number; year: number } => {
    const date = new Date(dateString);
    const calendarYear = date.getFullYear();

    let isoWeekStart = getISOWeekStart(calendarYear);

    if (date < isoWeekStart) {
      const prevYear = calendarYear - 1;
      isoWeekStart = getISOWeekStart(prevYear);

      const diffTime = date.getTime() - isoWeekStart.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const week = Math.floor(diffDays / 7) + 1;

      return { week, year: prevYear };
    };

    const nextYear = calendarYear + 1;
    const nextYearISOStart = getISOWeekStart(nextYear);

    if (date >= nextYearISOStart) {
      const diffTime = date.getTime() - nextYearISOStart.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const week = Math.floor(diffDays / 7) + 1;

      return { week, year: nextYear };
    };

    const diffTime = date.getTime() - isoWeekStart.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const week = Math.floor(diffDays / 7) + 1;

    return { week, year: calendarYear };
  };

  const getWeekFromDate = (dateString: string): number => {
    return getISOWeekAndYear(dateString).week;
  };

  const getDateOfWeek = (week: number, year: number) => {
    const isoWeekStart = getISOWeekStart(year);

    const startDate = new Date(isoWeekStart);
    startDate.setDate(isoWeekStart.getDate() + (week - 1) * 7);

    return startDate;
  };

  const formatWeekRange = (week: number, year: number) => {
    const startDate = getDateOfWeek(week, year);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    return `${startDate.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
    })} - ${endDate.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
    })}`;
  };

  const processHomeworkData = (homeworkGroups: any[]) => {
    const weekGroups: { [key: string]: any } = {};

    homeworkGroups.forEach((group) => {
      const lesson = group.lesson;
      const homeworks = group.items || [];

      homeworks.forEach((homework: any) => {
        const dateToUse = homework.deadline || homework.createdAt;
        if (!dateToUse) return;

        let week = getWeekFromDate(dateToUse);

        if (homework.metadata?.time && typeof homework.metadata.time === "number") {
          week = homework.metadata.time;
        } else {
          const titleMatch = homework.title?.match(/(\d+)\.\s*hafta/i);
          if (titleMatch) {
            week = parseInt(titleMatch[1]);
          }
        }

        let year: number;
        if (homework.metadata?.year && typeof homework.metadata.year === "number") year = homework.metadata.year;
        else year = getISOWeekAndYear(dateToUse).year;

        const weekKey = `${year}-W${week}`;

        if (!weekGroups[weekKey]) {
          weekGroups[weekKey] = {
            week,
            year,
            weekRange: formatWeekRange(week, year),
            lessons: {},
            days: {},
          };
        }

        // Kişisel programdaki ödev gün gün listelenir (HomeworkDayGroups); şube ödevi ders ders kalır
        const planDate = typeof homework.metadata?.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(homework.metadata.date) ? homework.metadata.date : null;
        if (planDate) {
          const days = weekGroups[weekKey].days;
          if (!days[planDate]) days[planDate] = { date: planDate, label: homework.metadata?.day || planDate, homeworks: [] };
          days[planDate].homeworks.push({ ...homework, lesson });
          return;
        }

        if (!weekGroups[weekKey].lessons[lesson.id]) {
          weekGroups[weekKey].lessons[lesson.id] = {
            lesson,
            homeworks: [],
          };
        }

        weekGroups[weekKey].lessons[lesson.id].homeworks.push(homework);
      });
    });

    return Object.entries(weekGroups)
      .sort(([, a], [, b]) => {
        const dateA = getDateOfWeek(a.week, a.year);
        const dateB = getDateOfWeek(b.week, b.year);
        return dateB.getTime() - dateA.getTime();
      })
      .map(([key, value]) => ({ key, ...value }));
  };

  // Hafta kartı sayıları: ders ders ve gün gün (kişisel program) listelenen ödevlerin toplamı
  const weekHomeworks = (weekGroup: any): any[] => [
    ...Object.values(weekGroup.lessons || {}).flatMap((l: any) => l.homeworks),
    ...Object.values(weekGroup.days || {}).flatMap((d: any) => d.homeworks),
  ];
  const weekLessonCount = (weekGroup: any) => new Set(weekHomeworks(weekGroup).map((h: any) => h.lessonId)).size;
  const openHomework = (homework: any) =>
    navigate(`/lessons/play/${homework.lessonId}?sectionId=${homework.lessonSectionId}&contentId=${homework.lessonSectionContentId}`);

  const homeworksTab: TabItemProps = useMemo(() => {
    const canListHomeworks = checkPermission("homework_list");

    const apiParams: any = {
      sortBy: "deadline",
      sortOrder: "DESC",
    };
    if (selectedHomeworkStatus !== "all") {
      apiParams.status = selectedHomeworkStatus;
    }
    if (homeworkStartDate) {
      apiParams.startDate = homeworkStartDate;
    }
    if (homeworkEndDate) {
      apiParams.endDate = homeworkEndDate;
    }

    const queryParams = new URLSearchParams(apiParams).toString();
    const apiPath = `homeworks?${queryParams}`;

    return {
      type: "list",
      title: "Ödevler",
      path: apiPath,
      tabId: "homeworks",
      isSearch: true,
      showToggle: true,
      actions: homeworksActions,
      leftArea: homeworkFilterOpen ? renderHomeworkStatusFilter : undefined,
      refreshKey: `homeworks-${homeworkRefreshTrigger}`,
      __visible: canListHomeworks as any,
      dataProcessor: processHomeworkData,
      item: (weekGroup: any, viewMode = "grid") => {
        if (viewMode === "grid") {
          return (
            <div
              key={weekGroup.key}
              className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-8 w-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{weekGroup.week}. Hafta ({weekGroup.year})</h3>
                    <p className="text-xs text-gray-600">{weekGroup.weekRange}</p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium bg-white border-purple-200 text-purple-700">
                      <BookOpen className="w-3.5 h-3.5" />
                      {weekLessonCount(weekGroup)} Ders
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium bg-white border-indigo-200 text-indigo-700">
                      <ClipboardCheck className="w-3.5 h-3.5" />
                      {weekHomeworks(weekGroup).length}{" "}
                      Ödev
                    </span>
                  </div>
                </div>
              </div>

              <div className="px-4 py-4">
                <div className="space-y-3 w-full">
                  <HomeworkDayGroups days={weekGroup.days} onOpen={openHomework} />
                  {Object.values(weekGroup.lessons).map((lessonData: any) => {
                    const lesson = lessonData.lesson;
                    const homeworks = lessonData.homeworks;
                    const expandKey = `${weekGroup.key}:${lesson.id}`;
                    const isExpanded = expandedLessonGroups[expandKey] ?? true;

                    if (homeworks.length === 0) return null;

                    return (
                      <div key={lesson.id} className="space-y-2">
                        <div
                          className="flex items-center gap-2 pb-1 border-b border-gray-100 cursor-pointer select-none"
                          onClick={() => toggleLessonGroup(weekGroup.key, lesson.id)}>
                          <BookOpen className="h-4 w-4 text-purple-600" />
                          <h4 className="font-semibold text-gray-900 text-base flex-1">{lesson.title}</h4>
                          <span className="inline-flex items-center justify-center bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                            {homeworks.length} Ödev
                          </span>
                          <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${isExpanded ? "rotate-90" : "rotate-0"}`} />
                        </div>

                        {isExpanded && (
                          <div className="flex flex-col items-center gap-2 pl-2">
                            {homeworks.map((homework: any) => {
                              const getHomeworkStatus = (status: string, deadline?: string) => {
                                if (deadline) {
                                  const now = new Date();
                                  const deadlineDate = new Date(deadline);
                                  const isOverdue = now > deadlineDate;

                                  if (isOverdue && (status === "PENDING" || status === "STARTED")) {
                                    return "Süresi Geçti";
                                  }
                                }

                                switch (status) {
                                  case "PENDING":
                                    return "Bekliyor";
                                  case "STARTED":
                                    return "Devam Ediyor";
                                  case "COMPLETED":
                                    return "Tamamlandı";
                                  case "FAILED":
                                    return "Başarısız";
                                  default:
                                    return "Bekliyor";
                                }
                              };

                              const getStatusBadgeClass = (status: string, deadline?: string) => {
                                if (deadline) {
                                  const now = new Date();
                                  const deadlineDate = new Date(deadline);
                                  const isOverdue = now > deadlineDate;

                                  if (isOverdue && (status === "PENDING" || status === "STARTED")) {
                                    return "bg-orange-100 text-orange-800 border-orange-200";
                                  }
                                }

                                switch (status) {
                                  case "PENDING":
                                    return "bg-yellow-100 text-yellow-800 border-yellow-200";
                                  case "STARTED":
                                    return "bg-blue-100 text-blue-800 border-blue-200";
                                  case "COMPLETED":
                                    return "bg-green-100 text-green-800 border-green-200";
                                  case "FAILED":
                                    return "bg-red-100 text-red-800 border-red-200";
                                  default:
                                    return "bg-gray-100 text-gray-800 border-gray-200";
                                }
                              };

                              const getContentIcon = () => {
                                const type = homework.lessonSectionContent?.type;
                                if (type === "VIDEO") {
                                  return <Video className="w-4 h-4 text-purple-600" />;
                                } else if (type === "TEST" || type === "EXAM") {
                                  return <ClipboardCheck className="w-4 h-4 text-emerald-600" />;
                                } else if (type === "FILE") {
                                  return <FileText className="w-4 h-4 text-blue-600" />;
                                } else {
                                  return <FileText className="w-4 h-4 text-gray-600" />;
                                }
                              };

                              return (
                                <div
                                  key={homework.id}
                                  className="max-w-sm w-full bg-white border border-gray-200 rounded-md p-3 hover:bg-gray-50 hover:border-purple-300 transition-all duration-200 cursor-pointer group"
                                  onClick={() => {
                                    navigate(
                                      `/lessons/play/${homework.lessonId}?sectionId=${homework.lessonSectionId}&contentId=${homework.lessonSectionContentId}`
                                    );
                                  }}>
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                      <div className="group-hover:scale-110 transition-transform flex-shrink-0">
                                        <Target className="h-4 w-4 text-indigo-600" />
                                      </div>
                                      <span className="text-sm text-gray-900 font-medium">
                                        {homework.lessonSection?.title || "(Bölüm Bulunamadı)"}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="group-hover:scale-110 transition-transform flex-shrink-0">{getContentIcon()}</div>
                                      <span className="text-sm text-gray-900 font-medium">
                                        {homework.lessonSectionContent?.title || "(İçerik Bulunamadı)"}
                                      </span>
                                    </div>

                                    {homework.deadline && (
                                      <div className="flex items-center justify-center gap-2 mt-2 pt-2 border-t border-gray-100">
                                        <div className="text-center">
                                          <div className="text-xs text-gray-500 font-medium">Bitiş Tarihi</div>
                                          <div className="text-xs text-gray-700 font-semibold">{formatDeadlineDate(homework.deadline)}</div>
                                        </div>
                                        <div className="w-px h-8 bg-gray-200"></div>
                                        <div className="text-center">
                                          <div className="text-xs text-gray-500 font-medium">Kalan Süre</div>
                                          <div
                                            className={`text-xs font-semibold ${getRemainingTime(homework.deadline) === "Süresi Geçti"
                                              ? "text-red-600"
                                              : getRemainingTime(homework.deadline) === "Bugün Bitiyor"
                                                ? "text-red-500"
                                                : getRemainingTime(homework.deadline).includes("Gün")
                                                  ? "text-orange-600"
                                                  : "text-green-600"
                                              }`}>
                                            {getRemainingTime(homework.deadline)}
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    <div className="flex justify-center">
                                      <span
                                        className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(
                                          homework.status,
                                          homework.deadline
                                        )}`}>
                                        {getHomeworkStatus(homework.status, homework.deadline)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 pt-3 border-t border-gray-200">
                  <div className="flex flex-wrap gap-1 justify-center">
                    {(() => {
                      const statusCounts = {
                        PENDING: 0,
                        STARTED: 0,
                        COMPLETED: 0,
                        FAILED: 0,
                      };
                      weekHomeworks(weekGroup).forEach((hw: any) => {
                        statusCounts[hw.status as keyof typeof statusCounts]++;
                      });

                      return Object.entries(statusCounts)
                        .filter(([, count]) => count > 0)
                        .map(([status, count]) => {
                          const statusLabels = {
                            PENDING: "Bekliyor",
                            STARTED: "Devam Ediyor",
                            COMPLETED: "Tamamlandı",
                            FAILED: "Başarısız",
                          };
                          const statusColors = {
                            PENDING: "bg-yellow-50 text-yellow-700 border-yellow-200",
                            STARTED: "bg-blue-50 text-blue-700 border-blue-200",
                            COMPLETED: "bg-green-50 text-green-700 border-green-200",
                            FAILED: "bg-red-50 text-red-700 border-red-200",
                          };

                          return (
                            <span
                              key={status}
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[status as keyof typeof statusColors]
                                }`}>
                              {count} {statusLabels[status as keyof typeof statusLabels]}
                            </span>
                          );
                        });
                    })()}
                  </div>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div
            key={weekGroup.key}
            className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden mb-4">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 px-4 py-3 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{weekGroup.week}. Hafta ({weekGroup.year})</h3>
                    <p className="text-xs text-gray-600">{weekGroup.weekRange}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center min-w-[48px]">
                    <div className="text-lg font-bold text-purple-700 text-center">{weekLessonCount(weekGroup)}</div>
                    <div className="text-xs text-purple-600 font-medium text-center">Ders</div>
                  </div>
                  <div className="text-center min-w-[48px]">
                    <div className="text-lg font-bold text-indigo-700 text-center">
                      {weekHomeworks(weekGroup).length}
                    </div>
                    <div className="text-xs text-indigo-600 font-medium text-center">Ödev</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-4 py-4">
              <div className="space-y-3">
                <HomeworkDayGroups days={weekGroup.days} onOpen={openHomework} />
                {Object.values(weekGroup.lessons).map((lessonData: any) => {
                  const lesson = lessonData.lesson;
                  const homeworks = lessonData.homeworks;
                  const expandKey = `${weekGroup.key}:${lesson.id}`;
                  const isExpanded = expandedLessonGroups[expandKey] ?? true;

                  if (homeworks.length === 0) return null;

                  return (
                    <div key={lesson.id} className="space-y-2">
                      <div
                        className="flex items-center gap-2 pb-1 border-b border-gray-100 cursor-pointer select-none"
                        onClick={() => toggleLessonGroup(weekGroup.key, lesson.id)}>
                        <BookOpen className="h-4 w-4 text-purple-600" />
                        <h4 className="font-semibold text-gray-900 text-base flex-1">{lesson.title}</h4>
                        <span className="inline-flex items-center justify-center bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                          {homeworks.length} Ödev
                        </span>
                        <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${isExpanded ? "rotate-90" : "rotate-0"}`} />
                      </div>

                      {isExpanded && (
                        <div className="space-y-1 pl-2">
                          {homeworks.map((homework: any) => {
                            const getHomeworkStatus = (status: string, deadline?: string) => {
                              if (deadline) {
                                const now = new Date();
                                const deadlineDate = new Date(deadline);
                                const isOverdue = now > deadlineDate;

                                if (isOverdue && (status === "PENDING" || status === "STARTED")) {
                                  return "Süresi Geçti";
                                }
                              }

                              switch (status) {
                                case "PENDING":
                                  return "Bekliyor";
                                case "STARTED":
                                  return "Devam Ediyor";
                                case "COMPLETED":
                                  return "Tamamlandı";
                                case "FAILED":
                                  return "Başarısız";
                                default:
                                  return "Bekliyor";
                              }
                            };

                            const getStatusBadgeClass = (status: string, deadline?: string) => {
                              if (deadline) {
                                const now = new Date();
                                const deadlineDate = new Date(deadline);
                                const isOverdue = now > deadlineDate;

                                if (isOverdue && (status === "PENDING" || status === "STARTED")) {
                                  return "bg-orange-100 text-orange-800 border-orange-200";
                                }
                              }

                              switch (status) {
                                case "PENDING":
                                  return "bg-yellow-100 text-yellow-800 border-yellow-200";
                                case "STARTED":
                                  return "bg-blue-100 text-blue-800 border-blue-200";
                                case "COMPLETED":
                                  return "bg-green-100 text-green-800 border-green-200";
                                case "FAILED":
                                  return "bg-red-100 text-red-800 border-red-200";
                                default:
                                  return "bg-gray-100 text-gray-800 border-gray-200";
                              }
                            };

                            const getContentIcon = () => {
                              const type = homework.lessonSectionContent?.type;
                              if (type === "VIDEO") {
                                return <Video className="w-4 h-4 text-purple-600" />;
                              } else if (type === "TEST" || type === "EXAM") {
                                return <ClipboardCheck className="w-4 h-4 text-emerald-600" />;
                              } else if (type === "FILE") {
                                return <FileText className="w-4 h-4 text-blue-600" />;
                              } else {
                                return <FileText className="w-4 h-4 text-gray-600" />;
                              }
                            };

                            return (
                              <div
                                key={homework.id}
                                className="bg-white border border-gray-200 rounded-md p-3 hover:bg-gray-50 hover:border-purple-300 transition-all duration-200 cursor-pointer group"
                                onClick={() => {
                                  navigate(
                                    `/lessons/play/${homework.lessonId}?sectionId=${homework.lessonSectionId}&contentId=${homework.lessonSectionContentId}`
                                  );
                                }}>
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex-1 space-y-1">
                                    <div className="flex items-center gap-2">
                                      <div className="group-hover:scale-110 transition-transform flex-shrink-0">
                                        <Target className="h-4 w-4 text-indigo-600" />
                                      </div>
                                      <span className="text-sm text-gray-900 font-medium">
                                        {homework.lessonSection?.title || "(Bölüm Bulunamadı)"}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="group-hover:scale-110 transition-transform flex-shrink-0">{getContentIcon()}</div>
                                      <span className="text-sm text-gray-900 font-medium">
                                        {homework.lessonSectionContent?.title || "(İçerik Bulunamadı)"}
                                      </span>
                                    </div>
                                  </div>

                                  {homework.deadline && (
                                    <div className="flex items-center gap-4 text-center">
                                      <div className="text-center">
                                        <div className="text-xs text-gray-500 font-medium">Bitiş Tarihi</div>
                                        <div className="text-xs text-gray-700 font-semibold">{formatDeadlineDate(homework.deadline)}</div>
                                      </div>
                                      <div className="text-center">
                                        <div className="text-xs text-gray-500 font-medium">Kalan Süre</div>
                                        <div
                                          className={`text-xs font-semibold ${getRemainingTime(homework.deadline) === "Süresi Geçti"
                                            ? "text-red-600"
                                            : getRemainingTime(homework.deadline) === "Bugün Bitiyor"
                                              ? "text-red-500"
                                              : getRemainingTime(homework.deadline).includes("Gün")
                                                ? "text-orange-600"
                                                : "text-green-600"
                                            }`}>
                                          {getRemainingTime(homework.deadline)}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  <div className="flex-shrink-0">
                                    <span
                                      className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(
                                        homework.status,
                                        homework.deadline
                                      )}`}>
                                      {getHomeworkStatus(homework.status, homework.deadline)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex flex-wrap gap-1 justify-center">
                  {(() => {
                    const statusCounts = {
                      PENDING: 0,
                      STARTED: 0,
                      COMPLETED: 0,
                      FAILED: 0,
                    };
                    weekHomeworks(weekGroup).forEach((hw: any) => {
                      statusCounts[hw.status as keyof typeof statusCounts]++;
                    });

                    return Object.entries(statusCounts)
                      .filter(([, count]) => count > 0)
                      .map(([status, count]) => {
                        const statusLabels = {
                          PENDING: "Bekliyor",
                          STARTED: "Devam Eden",
                          COMPLETED: "Tamamlanan",
                          FAILED: "Başarısız",
                        };
                        const statusColors = {
                          PENDING: "bg-yellow-50 text-yellow-700 border-yellow-200",
                          STARTED: "bg-blue-50 text-blue-700 border-blue-200",
                          COMPLETED: "bg-green-50 text-green-700 border-green-200",
                          FAILED: "bg-red-50 text-red-700 border-red-200",
                        };

                        return (
                          <span
                            key={status}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[status as keyof typeof statusColors]
                              }`}>
                            {count} {statusLabels[status as keyof typeof statusLabels]}
                          </span>
                        );
                      });
                  })()}
                </div>
              </div>
            </div>
          </div>
        );
      },
    };
  }, [
    selectedHomeworkStatus,
    homeworkStartDate,
    homeworkEndDate,
    homeworkRefreshTrigger,
    renderHomeworkStatusFilter,
    expandedLessonGroups,
    toggleLessonGroup,
    homeworkFilterOpen,
  ]);

  const tabs = useMemo(
    () => [coursesTab, homeworksTab, deficienciesTab, errorBookletTab, myReportsTab, reportsTab].filter((t: any) => t.__visible !== false),
    [coursesTab, homeworksTab, deficienciesTab, errorBookletTab, myReportsTab, reportsTab]
  );

  return <TabPage title="E-Eğitim" tabs={tabs} />;
}